import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr, pollJob } from "../lib/api";
import { Logo } from "../components/brand/Logo";
import { Button } from "../components/ui/button";
import { ScoreCard, MiniScore } from "../components/shared/ScoreCard";
import { DependencyCard } from "../components/shared/DependencyCard";
import { toast } from "sonner";
import { capture, captureOnce, EVENTS } from "../analytics/posthog";
import { useAbandonTracker } from "../analytics/useAbandon";
import { Send, Sparkles, ArrowRight, CheckCircle2, Loader2, Mic, Square, RotateCcw } from "lucide-react";

const STAGES = [
  "Mapeando responsabilidades…",
  "Analizando dependencias…",
  "Buscando puntos únicos de fallo…",
  "Evaluando backups…",
  "Calculando Independence Score…",
  "Construyendo tu Independence Plan…",
];

export default function Audit() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [phase, setPhase] = useState("chat"); // chat | analyzing | results
  const [stageIdx, setStageIdx] = useState(0);
  const [results, setResults] = useState(null);
  const endRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const initRef = useRef(false);
  const [searchParams] = useSearchParams();
  const [resumed, setResumed] = useState(false);
  const [starting, setStarting] = useState(false);

  const answers = messages.filter((m) => m.role === "user").length;
  const closeFlow = useAbandonTracker(EVENTS.auditAbandoned, () => ({ audit_id: sessionId, answers }));

  const startNew = async (explicit = false) => {
    setStarting(true);
    try {
      const { data } = await api.post("/audit/start");
      setSessionId(data.id); setMessages(data.messages); setResumed(false); setDone(false);
      captureOnce(EVENTS.auditStarted, data.id, { audit_id: data.id, explicit_new: explicit || undefined });
    } catch (e) {
      capture(EVENTS.auditStartFailed, { status: e?.response?.status || "network" });
      toast.error(apiErr(e));
    } finally { setStarting(false); }
  };

  // Resume the workspace's active audit; only create a new one if none exists or the user asked.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      if (searchParams.get("new") !== "1") {
        try {
          const { data } = await api.get("/audit/active");
          if (data && data.id) {
            setSessionId(data.id); setMessages(data.messages); setResumed(true);
            capture(EVENTS.auditResumed, { audit_id: data.id, answers: data.messages.filter((m) => m.role === "user").length });
            return;
          }
        } catch {}
      }
      await startNew(searchParams.get("new") === "1");
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput(""); setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const { data } = await api.post(`/audit/${sessionId}/message`, { content: text });
      setMessages((m) => [...m, { role: "assistant", content: data.message }]);
      if (data.done) { setDone(true); setSending(false); setTimeout(() => finalize(), 900); return; }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSending(false); }
  };

  const pollResults = async (attempts = 12, delay = 4000) => {
    for (let i = 0; i < attempts; i++) {
      try {
        const { data } = await api.get("/audit/results");
        if (data?.scores) return data;
      } catch {}
      await new Promise((r) => setTimeout(r, delay));
    }
    return null;
  };

  const finalize = async () => {
    setPhase("analyzing"); setStageIdx(0);
    const markCompleted = () => {
      closeFlow();
      captureOnce(EVENTS.auditCompleted, sessionId, { audit_id: sessionId });
      captureOnce(EVENTS.planGenerated, sessionId, { audit_id: sessionId });
    };
    try {
      const { data } = await api.post(`/audit/${sessionId}/finalize-async`);
      const job = await pollJob(data.job_id, (i) => setStageIdx(i));
      if (job.status === "DONE" && job.result?.scores) { setResults(job.result); setPhase("results"); markCompleted(); return; }
      throw new Error("job_failed");
    } catch (e) {
      const data = await pollResults();
      if (data) { setResults(data); setPhase("results"); markCompleted(); }
      else {
        capture(EVENTS.auditFinalizeFailed, { audit_id: sessionId, status: e?.response?.status || "job_failed" });
        toast.error(apiErr(e, "Architect no pudo completar el análisis. Tu conversación está guardada."));
        setPhase("chat");
      }
    }
  };

  const toggleRecording = async () => {
    if (recording) { recorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "audio.webm");
          const { data } = await api.post("/audit/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) setInput((prev) => (prev ? prev + " " : "") + data.text);
          else toast.error("No se detectó voz. Inténtalo de nuevo.");
        } catch (err) { toast.error(apiErr(err, "No se pudo transcribir el audio.")); }
        finally { setTranscribing(false); }
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
    }
  };

  // ---- Analyzing screen ----
  if (phase === "analyzing") {
    return (
      <div className="min-h-screen bg-obsidian grid-bg flex items-center justify-center px-4">
        <div className="w-full max-w-md card-arch p-8">
          <div className="halo" />
          <div className="relative flex items-center gap-2 text-cyan-400">
            <Sparkles className="h-5 w-5" /><span className="mono-label">ARCHITECT ESTÁ TRABAJANDO</span>
          </div>
          <div className="relative mt-6 space-y-3">
            {STAGES.map((s, i) => (
              <div key={s} className="flex items-center gap-3 text-sm">
                {i < stageIdx ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  : i === stageIdx ? <Loader2 className="h-4 w-4 text-amber-400 animate-spin" />
                  : <span className="h-4 w-4 rounded-full border border-white/15" />}
                <span className={i <= stageIdx ? "text-white" : "text-slate-500"}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---- Results screen (WOW) ----
  if (phase === "results" && results) {
    const s = results.scores;
    return (
      <div className="min-h-screen bg-obsidian grid-bg">
        <div className="p-6"><Logo /></div>
        <div className="mx-auto max-w-5xl px-4 pb-20">
          <div className="fade-up text-center">
            <span className="mono-label text-amber-400">Diagnóstico de Independencia</span>
            <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base text-slate-300">
              Tu empresa obtiene <b className="text-white">{s.independence_score}/100</b> en el Índice de Independencia
              ({s.independence_category}). {s.owner_dependency_score >= 60 ? "Dependes críticamente de ti en decisiones y control clave." : "La operación básica puede continuar sin ti, pero hay áreas importantes que aún concentran el control."}
              {s.critical_dependencies > 0 ? ` Se han detectado ${s.critical_dependencies} dependencia(s) crítica(s).` : ""}
            </p>
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-6 items-start">
            <div className="fade-up"><ScoreCard testId="scorecard-independence-score" score={s.independence_score} title="Índice de Independencia" category={s.independence_category} size={170} /></div>
            <div className="fade-up space-y-6" style={{ animationDelay: ".1s" }}>
              <MiniScore testId="score-owner-dependency" label="Dependencia del propietario" value={s.owner_dependency_score} tone="red" />
              <MiniScore testId="score-key-person" label="Riesgo por persona clave" value={s.key_person_risk} suffix="" tone="amber" />
            </div>
            <div className="fade-up space-y-6" style={{ animationDelay: ".2s" }}>
              <MiniScore testId="score-dependencies-count" label="Dependencias detectadas" value={s.dependencies_count} tone="cyan" />
              <MiniScore testId="score-critical" label="Críticas" value={s.critical_dependencies} tone="red" />
            </div>
          </div>

          <div className="mt-12 fade-up" style={{ animationDelay: ".25s" }}>
            <h2 className="font-head text-2xl font-bold text-white">Tus 3 dependencias principales</h2>
            <div className="mt-5 grid md:grid-cols-3 gap-5">
              {results.top_dependencies.map((d, i) => <DependencyCard key={d.id} dep={d} index={i} />)}
            </div>
          </div>

          <div className="mt-10 card-arch p-6 fade-up" style={{ animationDelay: ".3s" }}>
            <div className="halo-cyan" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-head text-lg font-semibold text-white">Tu Independence Plan está listo</h3>
                <p className="mt-1 text-sm text-slate-400">{results.plan.length} prioridades ordenadas por impacto y riesgo. Architect puede empezar a construir sistemas cuando quieras.</p>
              </div>
              <Button data-testid="audit-enter-app-button" onClick={() => navigate("/app")}
                className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold px-6 shrink-0">
                Entrar al panel <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Chat screen ----
  return (
    <div className="h-screen bg-obsidian flex flex-col">
      <header className="glass border-b border-white/10 px-4 sm:px-6 py-3 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-2 text-cyan-400"><Sparkles className="h-4 w-4" /><span className="mono-label">BUSINESS INDEPENDENCE AUDIT</span></div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-2xl px-4 py-8 space-y-5">
          {resumed && (
            <div data-testid="audit-resumed-banner" className="flex items-center justify-between gap-3 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.05] px-4 py-2.5 text-xs text-slate-300">
              <span>Continúas tu diagnóstico donde lo dejaste.</span>
              <button type="button" data-testid="audit-start-new-button" disabled={starting}
                onClick={() => { if (window.confirm("¿Empezar un diagnóstico nuevo? El actual se conserva en tu historial.")) startNew(true); }}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1.5 font-medium text-white hover:bg-white/[0.1] disabled:opacity-50">
                <RotateCcw className="h-3.5 w-3.5" /> Empezar uno nuevo
              </button>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`audit-message-${m.role}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user" ? "bg-amber-500 text-black rounded-br-sm"
                                   : "card-arch text-slate-200 rounded-bl-sm"}`}>
                {m.role === "assistant" && <span className="mono-label text-cyan-400 block mb-1">ARCHITECT</span>}
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="card-arch rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex gap-1.5">
                  {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 rounded-full bg-cyan-400 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />)}
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div className="glass border-t border-white/10 px-4 py-4 pb-safe">
        <div className="mx-auto max-w-2xl">
          {(done || answers >= 4) && (
            <div className="mb-3 flex justify-center">
              <Button data-testid="audit-finalize-button" onClick={finalize}
                className="bg-emerald-500/90 text-black hover:bg-emerald-400 rounded-full font-semibold">
                <Sparkles className="mr-2 h-4 w-4" /> Generar mi diagnóstico
              </Button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              data-testid="audit-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={recording ? "Grabando… toca el micro para parar" : transcribing ? "Transcribiendo tu voz…" : "Responde con naturalidad o usa el micrófono…"}
              className="flex-1 resize-none rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40 max-h-32"
            />
            <Button data-testid="audit-mic-button" onClick={toggleRecording} disabled={transcribing}
              className={`h-11 w-11 shrink-0 rounded-xl p-0 ${recording ? "bg-red-500 text-white hover:bg-red-400" : "bg-white/[0.06] text-white hover:bg-white/[0.1]"}`}
              title="Responder por voz">
              {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <Button data-testid="audit-send-button" onClick={send} disabled={sending || !input.trim()}
              className="h-11 w-11 shrink-0 rounded-xl bg-amber-500 text-black hover:bg-amber-400 p-0">
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-2 text-center text-[0.7rem] text-slate-600">Al usar el micrófono aceptas que tu audio se transcriba de forma segura. No se almacena la grabación.</p>
        </div>
      </div>
    </div>
  );
}
